import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, ChevronsUpDown, Check, Loader2 } from "lucide-react";
import {
  COUNTRY_CODES, DEFAULT_COUNTRY, formatE164, isValidPhone,
} from "../lib/countryCodes";
import { useCompany } from "../contexts/CompanyContext";
import { useTheme } from "../contexts/ThemeContext";

// Email RFC 5322 "simplificada" — cubre >99% de los correos reales sin
// introducir falsos negativos. Pragmática, no perfectamente spec-compliant.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function Register() {
  const { register } = useAuth();
  const { heroImageUrl, heroImageUrlDark } = useCompany();
  const { theme } = useTheme();
  const activeHero = theme === "dark" ? (heroImageUrlDark || heroImageUrl) : heroImageUrl;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",           // solo dígitos locales, sin prefijo
    password: "",
    passwordConfirm: "",
  });
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check de "correo ya registrado" con debounce. `null` = aún no
  // consultado, `true` = disponible, `false` = tomado. Sólo consulta si
  // el correo pasa el regex local, para no quemar requests en cada tecla.
  const [emailStatus, setEmailStatus] = useState({ state: "idle" }); // idle|checking|available|taken
  useEffect(() => {
    const e = form.email.trim().toLowerCase();
    if (!e || !EMAIL_RE.test(e)) {
      setEmailStatus({ state: "idle" });
      return;
    }
    setEmailStatus({ state: "checking" });
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/email-available`, { params: { email: e } });
        setEmailStatus({ state: res.data?.available ? "available" : "taken" });
      } catch {
        // Falla silenciosa — no bloqueamos el submit, el backend validará.
        setEmailStatus({ state: "idle" });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.email]);

  // Validaciones derivadas — se muestran como hints bajo cada campo y
  // también controlan el estado `disabled` del botón de submit.
  const errors = useMemo(() => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Ingresa tu nombre completo.";
    if (!form.email) e.email = "Correo obligatorio.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Correo no válido.";
    else if (emailStatus.state === "taken") e.email = "Este correo ya está registrado.";
    if (!form.phone) e.phone = "Teléfono obligatorio.";
    else if (!isValidPhone(country, form.phone)) {
      const [mi, ma] = country.length;
      e.phone = mi === ma
        ? `Teléfono debe tener ${mi} dígitos.`
        : `Teléfono debe tener entre ${mi} y ${ma} dígitos.`;
    }
    if (form.password.length < 8) e.password = "Mínimo 8 caracteres.";
    if (!form.passwordConfirm) e.passwordConfirm = "Confirma tu contraseña.";
    else if (form.password !== form.passwordConfirm) e.passwordConfirm = "Las contraseñas no coinciden.";
    return e;
  }, [form, country, emailStatus]);

  const canSubmit = Object.keys(errors).length === 0 && !loading && emailStatus.state !== "checking";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Revisa los campos marcados");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        // E.164 listo para backend y WhatsApp Bot — sin espacios ni guiones.
        phone: formatE164(country, form.phone),
        password: form.password,
      });
      toast.success("Cuenta creada");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al registrarte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
      <div className="w-full max-w-md bg-white rounded-2xl border border-neutral-200 p-8">
        {/* Header: imagen del landing (respeta light/dark) centrada y
            clickable → home. Reemplaza favicon + wordmark. */}
        {activeHero && (
          <Link to="/" className="block mx-auto mb-6 w-fit" data-testid="register-hero-link">
            <img
              src={activeHero}
              alt="Inicio"
              className="max-h-16 sm:max-h-20 w-auto max-w-full h-auto object-contain select-none"
              draggable={false}
              data-testid="register-hero-image"
            />
          </Link>
        )}
        <h1 className="text-3xl font-display font-extrabold tracking-tight text-center">Crea tu cuenta</h1>
        <p className="text-sm text-neutral-500 mt-1 text-center">Solo toma 30 segundos.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          {/* Nombre */}
          <div>
            <Label>Nombre completo</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="h-12"
              data-testid="register-name-input"
            />
          </div>

          {/* Correo */}
          <div>
            <Label>Correo</Label>
            <div className="relative">
              <Input
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="h-12 pr-10"
                data-testid="register-email-input"
              />
              {/* Indicador debounced: spinner mientras consulta, check si disponible.
                  No mostramos ícono si el email aún no es válido localmente. */}
              {form.email && EMAIL_RE.test(form.email.trim()) && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid={`email-status-${emailStatus.state}`}>
                  {emailStatus.state === "checking" && <Loader2 size={18} className="animate-spin text-neutral-400" />}
                  {emailStatus.state === "available" && <Check size={18} className="text-emerald-600" />}
                </span>
              )}
            </div>
            {form.email && errors.email && (
              <p className="mt-1 text-xs text-red-600" data-testid="register-email-error">{errors.email}</p>
            )}
          </div>

          {/* Teléfono — dropdown de lada + input numérico local */}
          <div>
            <Label>Teléfono</Label>
            <div className="flex gap-2">
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={countryOpen}
                    className="h-12 px-3 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center gap-1.5 shrink-0 text-sm font-medium outline-none focus:ring-2 focus:ring-offset-1 focus:ring-neutral-300"
                    data-testid="country-dial-trigger"
                  >
                    <span className="text-lg leading-none">{country.flag}</span>
                    <span className="tabular-nums">{country.dial}</span>
                    <ChevronsUpDown size={14} className="text-neutral-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[280px]" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar país…" />
                    <CommandList>
                      <CommandEmpty>Sin resultados.</CommandEmpty>
                      <CommandGroup>
                        {COUNTRY_CODES.map((c) => (
                          <CommandItem
                            key={c.code}
                            value={`${c.name} ${c.dial} ${c.code}`}
                            onSelect={() => {
                              setCountry(c);
                              setCountryOpen(false);
                            }}
                            data-testid={`country-option-${c.code}`}
                          >
                            <span className="text-lg mr-2">{c.flag}</span>
                            <span className="flex-1">{c.name}</span>
                            <span className="text-xs text-neutral-500 tabular-nums mr-2">{c.dial}</span>
                            {country.code === c.code && <Check size={14} />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="10 dígitos"
                value={form.phone}
                onChange={(e) =>
                  setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 15) })
                }
                required
                className="h-12 flex-1"
                data-testid="register-phone-input"
              />
            </div>
            {form.phone && errors.phone && (
              <p className="mt-1 text-xs text-red-600" data-testid="register-phone-error">{errors.phone}</p>
            )}
          </div>

          {/* Contraseña con ojo */}
          <div>
            <Label>Contraseña</Label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="h-12 pr-11"
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-neutral-500 hover:text-neutral-800"
                aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                data-testid="register-password-toggle"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.password && errors.password && (
              <p className="mt-1 text-xs text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Confirmación contraseña con su propio ojo */}
          <div>
            <Label>Confirmar contraseña</Label>
            <div className="relative">
              <Input
                type={showPwd2 ? "text" : "password"}
                autoComplete="new-password"
                value={form.passwordConfirm}
                onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })}
                required
                className="h-12 pr-11"
                data-testid="register-password-confirm-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd2((v) => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-neutral-500 hover:text-neutral-800"
                aria-label={showPwd2 ? "Ocultar contraseña" : "Mostrar contraseña"}
                data-testid="register-password-confirm-toggle"
              >
                {showPwd2 ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.passwordConfirm && errors.passwordConfirm && (
              <p className="mt-1 text-xs text-red-600" data-testid="register-password-confirm-error">{errors.passwordConfirm}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-12 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl text-base font-semibold"
            data-testid="register-submit-btn"
          >
            {loading ? "Creando..." : "Crear cuenta"}
          </Button>
        </form>
        <p className="text-sm text-neutral-500 mt-6 text-center">
          ¿Ya tienes cuenta? <Link to="/login" className="font-semibold text-black dark:text-neutral-100" data-testid="go-login-link">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
